import React, { useRef, useEffect, useMemo, useCallback, forwardRef } from "react";
import { useJackpotPlates } from "../../../Games/Shared/jackpotPlates";
import { Container, Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useSelector, useDispatch } from "react-redux";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../pixiSettings";
import {
  useSlotRegistry,
  useSlot,
  getCounterScale,
} from "../../../Contexts/SlotRegistryContext";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import {
  selectFreeSpinsActive,
  selectFreeSpinsReloadPhase,
  selectShowGameAfterTransition,
  selectIntroPopupActive,
} from "../../../State/selectors";
import {
  setShowFreeSpinIntro,
  setShowGambleDialog,
  setCriticalAnimationPlaying,
  setUserInterfaceDisabled,
  setShowFreeSpinsReload,
  setShowFreeSpinTotalWin,
  setShowFreeSpinZeroWin,
  setShowFreeSpinMaxWin,
  setFeatureSpinActive,
} from "../../../State/actions";
import GameViewSimpleController from "./GameViewSimpleController";
import { selectHoldAndWinActive } from "../../../State/Slices/holdAndWinSlice";
import LevelChargingAnimator from "../../../Engine/PotCharging/LevelChargingAnimator";
import ReelSet from "../../../Games/Main/ReelSet/ReelSet";
import OverlayReelSet from "../../../Games/Main/OverlayReelSet/OverlayReelSet";
import DimReelSet from "../../../Games/Shared/DimReelSet/DimReelSet";
import WinBoxContainer from "../../../Games/Shared/WinBoxReelSet/WinBoxContainer";
import WinText from "../WinText/WinText";
import { WIN_TEXT_CONFIG } from "../WinText/WinTextController";
import {
  attemptFullscreenToggle,
  isPhysicalDesktop,
  isInFullscreen,
} from "../../../utilities";
import store from "../../../State/Store/store";
import { fsDiag } from "../../../utils/fsDiag";
import TickerTimeout from "../../../utils/TickerTimeout";
import {
  selectFreeSpinOutroPopup,
  FS_OUTRO_POPUP,
} from "../../../Games/Shared/freeSpinOutroPopup";

// Fine-tuning offsets for the injected containers, in spine-local pixels.
// Both containers stack on the same grid, so they must move together.
const REELSET_X_OFFSET = 0;
const REELSET_Y_OFFSET = 0;

// Background taps enter fullscreen. Rate-limited because a tap on a touch
// device can arrive as several events.
const MIN_BACKGROUND_CLICK_GAP = 100;

// How long a completion reload waits for the restored round's reveal to take
// the screen before it gives up and opens the outro anyway — 50 polls at the
// 100ms tick, so five seconds. Generous, because the reveal cannot start until
// the reels are built; bounded, because a round with no way out is worse than
// a dialog that arrives early.
const REVEAL_START_MAX_WAITS = 50;

/**
 * GameViewSimple — the Mad Labs scene.
 *
 * Renders one game_view spine and injects two things into it: the spinning
 * reels and the spine overlay above them. Everything else the old GameView
 * carried — pots, jackpots, respin counter, cloud transition, anticipation,
 * rain — does not exist in this game.
 *
 * The injected containers are counter-scaled. They are children of the spine,
 * so they would otherwise inherit its scale on top of their own, and the reels
 * already size themselves from symbolWidth/symbolHeight.
 */
const GameViewSimple = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const controllerRef = useRef(null);
  // The jackpot plates' values, on the current bet (Games/Shared/jackpotPlates).
  useJackpotPlates(controllerRef);
  const containerRef = useRef(null);
  const reelContainerRef = useRef(null);
  const overlayContainerRef = useRef(null);
  const goldenTrailContainerRef = useRef(null);
  const lastBackgroundClickRef = useRef(0);

  const {
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    dividerDimensions,
    reelSpacing,
    device,
    orientation,
    // The resolved layout key — finer than device + orientation, because it
    // splits desktop into small / standard / wide / ultra-wide. It is what
    // GAME_VIEW_OFFSET_CONFIG is keyed by.
    layout,
    isMobileSmall,
  } = useDimensions();

  const slotRegistry = useSlotRegistry();
  const {
    dimReelSetRef,
    globalLineWinTextRef,
    lineWinTextRef,
    winBoxContainerRef,
    gameViewControllerRef,
    goldenTrailAnimatorRef,
  } = useReelRefs();

  const fullscreenSupported = useSelector(
    (state) => state.settings.fullscreenSupported,
  );

  // The two rooms share a layer and take turns. This one is the base room, so
  // it steps aside for the whole free spins round — taking the board injected
  // into its spine with it, which is what we want; the free-spin board is a
  // separate one.
  //
  // Hidden by ALPHA, never by `visible`. PIXI skips transform AND render for
  // an invisible subtree, so the spine, its slot objects and its atlas all go
  // cold — and the frame it comes back is the one that has to pay for all of
  // it at once. That was the hitch on the transition. Every popup in this game
  // hides the same way, and so does reel_container in GameContainer.
  const freeSpinsActive = useSelector(selectFreeSpinsActive);
  // Hold & Win takes the screen the same way (its own slice).
  const holdAndWinActive = useSelector(selectHoldAndWinActive);

  // ── Reload recovery ──
  //
  // Same place Pot Collection keeps it: the provider derives the phase, the
  // slice records it, and the SCENE puts the matching screen up. See the
  // mid-bonus effect in GameView.jsx — this is the free-spins twin of it.
  const freeSpinsReloadPhase = useSelector(selectFreeSpinsReloadPhase);
  const showGameAfterTransition = useSelector(selectShowGameAfterTransition);
  // One-shot, exactly like GameView's midBonusSetupDoneRef. A boot step must
  // not re-run on a render and reopen a screen already answered.
  const freeSpinsRecoveryDoneRef = useRef(false);

  // ── ...and re-armed when the feature goes back to idle ──
  //
  // GameView clears midBonusSetupDoneRef the same way, commented "re-runs
  // after ErrorRecovery rehydrates PC". Only the set half was copied here, and
  // the missing half was a dead round: every refreshGameData re-arms
  // reloadPopupPending, but with the ref already spent no screen was ever put
  // up to clear it, so handleResume refused for ever and the loop never span
  // again. Recovery could work once per page load and never twice.
  useEffect(() => {
    if (!freeSpinsActive && freeSpinsReloadPhase === "none") {
      freeSpinsRecoveryDoneRef.current = false;
    }
  }, [freeSpinsActive, freeSpinsReloadPhase]);

  // Physical device capability is stable for the session.
  const isActualDesktop = useMemo(() => isPhysicalDesktop(), []);

  // The click catcher only ever does something when this is true. Without the
  // check it would show a pointer cursor over the whole game on desktop, where
  // clicking it does nothing.
  const backgroundClickIsActionable =
    !isActualDesktop && fullscreenSupported && !isInFullscreen();

  const spineCounterScale = useMemo(
    () => getCounterScale(symbolScale),
    [symbolScale],
  );

  // ─── Reload recovery ───
  //
  // The free-spins twin of GameView's mid-bonus setup effect, and deliberately
  // the same shape: wait for the boot gate, run once, and only put a screen up
  // — never send anything. The next command always comes from the player.
  useEffect(() => {
    if (freeSpinsRecoveryDoneRef.current) return;
    if (!showGameAfterTransition) {
      fsDiag("scene.reload waiting for showGameAfterTransition", {
        freeSpinsReloadPhase,
      });
      return;
    }
    if (freeSpinsReloadPhase === "none") return;

    fsDiag("scene.reload branch", {
      phase: freeSpinsReloadPhase,
      active: store.getState().freeSpins.active,
      awarded: store.getState().freeSpins.awarded,
      played: store.getState().freeSpins.played,
      totalWin: store.getState().freeSpins.totalWin,
      roundWin: store.getState().freeSpins.currentRound?.win ?? null,
    });

    // ── MID-ROUND: no entry sequence, just pick the round back up ──
    //
    // Nothing to replay. freeSpinsSlice has already hydrated the round straight
    // off the init — awarded, played, the running total, the room, the collector
    // and the last screen — and set `active`, so the bonus board is on screen
    // from the first frame with no wipe. NONE of that touches localStorage: the
    // API sends `outcome.screen` once spins have been played, so clearing
    // storage cannot stop the round coming back, exactly as for Pot Collection.
    //
    // All that is left is what Pot Collection does here: hold the UI, put the
    // "FREE SPINS / n / LEFT" popup up, and let the player's tap start play. The
    // popup's own close hands back criticalAnimationPlaying and
    // userInterfaceDisabled, and the loop is gated on the first of those — so
    // the round cannot resume until it is dismissed.
    if (freeSpinsReloadPhase === "in_progress") {
      freeSpinsRecoveryDoneRef.current = true;

      // Route spins to the featureSpin* flags. Entering through the wipe, the
      // transition midpoint does this; there is no wipe on this path, so
      // without it the first free spin would drive the BASE game's isSpinning /
      // spinComplete and the bonus loop would never see its own.
      dispatch(setFeatureSpinActive(true));

      dispatch(setCriticalAnimationPlaying(true));
      dispatch(setUserInterfaceDisabled(true));
      dispatch(setShowFreeSpinsReload(true));
      return;
    }

    // ── COMPLETION: the round closed, the total win was never shown ──
    //
    // Nothing to put up and nothing to hold. The slice hydrated the room as
    // active with no spins left, so the round loop's own `remaining <= 0`
    // branch opens the total win dialog after OUTRO_DELAY and closing it
    // starts the wipe back, exactly as a live last spin does. Locking the UI
    // here would only fight it.
    //
    // This branch also exists so a phase this effect does not know cannot fall
    // through to the entry replay below and lock the screen.
    if (freeSpinsReloadPhase === "completion") {
      freeSpinsRecoveryDoneRef.current = true;

      // ── The SCENE owns the outro on a reload ──
      //
      // GameView does this for Pot Collection: the completion phase is put on
      // screen by the scene, not by the feature's own round loop. Ours was
      // leaning on the loop's out-of-spins branch, which only runs once all
      // nine of its gates settle — and on a boot that is not guaranteed. A
      // no-win last round then left the player in the room with no dialog and
      // no way out, which is precisely a stuck session.
      //
      // Only when there is NOTHING to replay. If the restored round paid, the
      // win presentation must run first and the loop's own counter gate is the
      // correct thing to wait on — so ownership stays there for that case and
      // the two can never both fire.
      const restoredRound = store.getState().freeSpins.currentRound;
      const restoredWin = restoredRound?.win || 0;
      // The reveal runs on a LOSING board too, and it is the only thing there
      // is to wait for on one. `mystery_feature` is what the reveal reads, so
      // its presence is exactly "this board has something to open".
      const hasReveal = !!restoredRound?.storage?.mystery_feature;
      // Latched when the reveal actually takes the screen. Without it the poll
      // can read a clear board in the gap between the scene coming up and the
      // reveal effect raising its flag — the same race the live loop answers by
      // re-checking at fire time.
      let sawReveal = false;
      let revealWaits = 0;
      fsDiag("scene.reload COMPLETION", {
        restoredWin,
        hasReveal,
        // The scene opens it either way now; a replay only delays it.
        waitsForWinReplay: restoredWin > 0,
      });

      // ── Wait for the intro, then for the win, then open ──
      //
      // GameView's showReloadPopupWhenReady does exactly this for Pot
      // Collection: poll on a TickerTimeout until the screen is free, and only
      // then put the feature's screen up. Dispatched straight away it landed
      // on top of the intro, and the sequence played where nobody could see
      // it. The order the player must see is the room, then the win, then the
      // total.
      //
      // Only the INTRO popup here. The info popup is a free-rounds concern and
      // has no part in this handover.
      //
      // The second wait is what makes a WINNING last round work. Its win
      // replays through the normal presentation, and this dialog must not
      // arrive on top of it: the tests are the two the live loop uses to pace
      // its own outro, so a restored round and a played one hand over at the
      // same moment. A big win holds winAnimationsPlaying until the player
      // dismisses it; an ordinary win holds counterAnimationFinished until its
      // display window is over, with the line cycling continuing behind the
      // dialog exactly as it does in the base game.
      //
      // This is the ONLY opener in the completion phase — the loop defers to
      // it (see the reloadPhase check in FreeSpinBasicControlContext), so a
      // winning round cannot be left with nobody to open its total.
      const openOutroWhenReady = () => {
        const s = store.getState();
        // The intro, and the free-rounds message a campaign reload opens on —
        // the room's reveal waits for that one too, so the outro must not open
        // under it, or ahead of a reveal that has not been allowed to start.
        if (selectIntroPopupActive(s) || s.userInterface?.infoPopupActive) {
          new TickerTimeout(openOutroWhenReady, 100);
          return;
        }
        const anim = s.animations;

        // ── The mystery reveal and the Golden Mystery hold THIS one ──
        //
        // The wait used to be the two win flags only, and neither of them is
        // raised by a reveal — so the Total Win dialog arrived on top of the
        // doors opening and the coins landing, both running at once. It is the
        // live loop's own third gate; using the same flag means the scene and
        // the loop cannot disagree about when a round is still presenting.
        if (anim.criticalAnimationPlaying) {
          sawReveal = true;
          new TickerTimeout(openOutroWhenReady, 100);
          return;
        }

        // ── A reveal that already FINISHED has been seen too ──
        //
        // The room's win presentation starts only once the reveal has
        // published, and on a reload the counter starts from false — so this
        // round's counter having started proves the reveal is done. Without it
        // a reveal that ran and ended before this poll's first tick was never
        // "seen", and the outro sat out the whole five-second bound below with
        // the lines cycling. Vladimir, 2026-09-24: *"it runs like through 3
        // lines before it shows it."*
        if (anim.counterAnimationStarted) sawReveal = true;

        // Not before it has STARTED, when the board has one to run. Bounded:
        // a reveal that never arrives must not leave the player in the room
        // with no dialog and no way out, which is the stuck session this whole
        // branch exists to prevent.
        if (hasReveal && !sawReveal && revealWaits < REVEAL_START_MAX_WAITS) {
          revealWaits += 1;
          new TickerTimeout(openOutroWhenReady, 100);
          return;
        }

        // `counterAnimationStarted` as well as finished, for the same race:
        // the counter for THIS round has not begun in the frames right after
        // the reveal ends, so "finished" is still true from the spin before it.
        if (
          restoredWin > 0 &&
          (anim.winAnimationsPlaying ||
            !anim.counterAnimationStarted ||
            !anim.counterAnimationFinished)
        ) {
          new TickerTimeout(openOutroWhenReady, 100);
          return;
        }
        fsDiag("scene.reload COMPLETION screen free -> outro", {
          restoredWin,
          sawReveal,
        });
        // The same beat the live round takes between its last spin landing and
        // the dialog — FreeSpinBasicControlContext's OUTRO_DELAY — so a
        // restored round is paced like a played one.
        // A recovered round must close the same way a live one does — MAX WIN
        // and BETTER LUCK NEXT TIME included. Both are recomputed from the
        // response (winLimitReached, and totalWin against the stake), so they
        // survive the reload without storage.
        //
        // Same selector as the live loop in FreeSpinBasicControlContext, so
        // the two cannot disagree about which popup a round earned.
        const outroPopup = selectFreeSpinOutroPopup(store.getState());
        const outroAction =
          outroPopup === FS_OUTRO_POPUP.MAX
            ? setShowFreeSpinMaxWin
            : outroPopup === FS_OUTRO_POPUP.ZERO
              ? setShowFreeSpinZeroWin
              : setShowFreeSpinTotalWin;
        new TickerTimeout(() => dispatch(outroAction(true)), 800);
      };
      new TickerTimeout(openOutroWhenReady, 100);
      // Explicitly UNLOCKED. Every other reload branch raises these and hands
      // them to whichever screen takes over; this one has no screen to hand to
      // — the win replay and then the total win dialog are driven by the round
      // loop, and the loop's third gate is criticalAnimationPlaying. Anything
      // left over from the boot would park it here for good.
      dispatch(setCriticalAnimationPlaying(false));
      dispatch(setUserInterfaceDisabled(false));
      return;
    }

    if (freeSpinsReloadPhase !== "intro") return;

    freeSpinsRecoveryDoneRef.current = true;

    // Held until whichever screen takes over hands back — TransitionDialog at
    // its midpoint, or the gamble dialog if the package is lost.
    dispatch(setCriticalAnimationPlaying(true));
    dispatch(setUserInterfaceDisabled(true));

    // ── Replay the WHOLE entry sequence, not just its last screen ──
    //
    // The intro phase runs the same way Pot Collection's intro reload does:
    // the board comes back, the symbol plays its full sequence, the win is
    // presented, and the feature screen opens off that. WHICH screen is one
    // rule, applied in openFeatureScreen: the gamble screen while the ladder
    // is unanswered and has outcomes left; the intro once it is settled —
    // an emptied `variants` is the server recording that the player already
    // answered, and the screen must not be forced on them again.
    //
    //   base game -> symbol sequence -> [gamble screen] -> intro -> transition
    //
    // — identical on every reload until the phase advances. The opener off
    // the win is useWinController's openFeatureScreen; opening a second one
    // from here is what previously put an intro popup behind a live gamble.
    //
    // So there is nothing to dispatch for either phase — the trigger round is
    // already restored, by the provider from `outcome.screen` while the state
    // is gamble_freespins, and from the saved base round afterwards, when the
    // payload no longer carries a board.
    if (store.getState().game.currentRound?.triggerWin) return;

    // Only if that failed. Without a trigger round nothing will present, and
    // the player would sit in the base game holding a package they cannot
    // reach — so the right screen opens instantly, by the same rule
    // useWinController's openFeatureScreen applies: the gamble screen only
    // while the ladder is unanswered AND has outcomes left; otherwise the
    // intro, because a settled package leaves the gamble screen with no
    // data to offer.
    console.warn(
      "[FreeSpins] Reload: no trigger round to replay, opening the feature " +
        "screen directly. Phase:",
      freeSpinsReloadPhase,
    );
    const fs = store.getState().freeSpins;
    const canGamble =
      !fs.gambleSettled && (fs.gamble?.variants?.length ?? 0) > 0;
    dispatch(canGamble ? setShowGambleDialog(true) : setShowFreeSpinIntro(true));
  }, [showGameAfterTransition, freeSpinsReloadPhase, dispatch]);

  // ─── Controller ───
  useEffect(() => {
    if (!containerRef.current || controllerRef.current) return;

    const realDevice = isMobileSmall ? PixiSettings.DEVICE.MOBILE_SMALL : device;

    controllerRef.current = new GameViewSimpleController(
      containerRef.current,
      dimensions,
      symbolWidth,
      symbolHeight,
      symbolScale,
      realDevice,
      orientation,
      layout,
      slotRegistry,
    );

    // Shared so other components can reach the scene.
    if (gameViewControllerRef) {
      gameViewControllerRef.current = controllerRef.current;
    }

    return () => {
      if (controllerRef.current) {
        controllerRef.current.destroy();
        controllerRef.current = null;
        if (gameViewControllerRef) gameViewControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Golden Mystery trail animator ───
  //
  // The same trail the free-spin level boxes use — LevelChargingAnimator — fired
  // straight from one cell to another. No spine is passed because both endpoints
  // are GRID points: the multiplier's cell and the coin's, read off the overlay
  // containers. There are no bones to look up.
  //
  // No live-dimensions getter either, for the same reason. BonusGameView needs
  // one because its scene spine is rebuilt on resize and its box bones move with
  // it; nothing here is read from a spine, so the pushed resize below is the
  // whole story.
  useEffect(() => {
    if (!goldenTrailContainerRef.current) return;

    const animator = new LevelChargingAnimator(
      goldenTrailContainerRef.current,
      null,
      symbolWidth,
      symbolHeight,
    );
    if (goldenTrailAnimatorRef) goldenTrailAnimatorRef.current = animator;

    return () => {
      animator.destroy();
      if (goldenTrailAnimatorRef?.current === animator) {
        goldenTrailAnimatorRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The particle scale and the flight distance are both measured in cells.
  useEffect(() => {
    goldenTrailAnimatorRef?.current?.resize(symbolWidth, symbolHeight);
  }, [goldenTrailAnimatorRef, symbolWidth, symbolHeight]);

  // ─── Inject the reel container into the spine's reel bone ───
  const reelSlot = useSlot("gameView.reelSet");
  useEffect(() => {
    if (!reelSlot || !reelContainerRef.current) return;
    const node = reelContainerRef.current;
    reelSlot.addChild(node);
    return () => {
      if (node.parent === reelSlot) reelSlot.removeChild(node);
    };
  }, [reelSlot]);

  // ─── Inject the overlay container into the spine's overlay slot ───
  const overlaySlot = useSlot("gameView.overlayReelSet");
  useEffect(() => {
    if (!overlaySlot || !overlayContainerRef.current) return;
    const node = overlayContainerRef.current;
    overlaySlot.addChild(node);
    return () => {
      if (node.parent === overlaySlot) overlaySlot.removeChild(node);
    };
  }, [overlaySlot]);

  // ─── Undo the spine's inherited scale on both injected containers ───
  useEffect(() => {
    if (reelContainerRef.current) {
      reelContainerRef.current.scale.set(spineCounterScale);
    }
    if (overlayContainerRef.current) {
      overlayContainerRef.current.scale.set(spineCounterScale);
    }
  }, [spineCounterScale]);

  // ─── Resize ───
  useEffect(() => {
    if (!controllerRef.current) return;
    const realDevice = isMobileSmall ? PixiSettings.DEVICE.MOBILE_SMALL : device;
    controllerRef.current.resize(
      dimensions,
      symbolWidth,
      symbolHeight,
      symbolScale,
      realDevice,
      orientation,
      layout,
    );
  }, [
    dimensions,
    symbolWidth,
    symbolHeight,
    symbolScale,
    device,
    orientation,
    layout,
    isMobileSmall,
  ]);

  // ─── Background tap → fullscreen ───
  const handleBackgroundClick = useCallback(
    (event) => {
      const now = Date.now();
      if (now - lastBackgroundClickRef.current < MIN_BACKGROUND_CLICK_GAP) return;
      lastBackgroundClickRef.current = now;

      if (event?.stopPropagation) event.stopPropagation();

      if (
        !isPhysicalDesktop() &&
        !isInFullscreen() &&
        store.getState().settings.fullscreenSupported
      ) {
        attemptFullscreenToggle(dispatch, device);
      }
    },
    [dispatch, device],
  );

  return (
    <Container
      ref={containerRef}
      name="game_view"
      sortableChildren
      zIndex={0}
      alpha={freeSpinsActive || holdAndWinActive ? 0.001 : 1}
    >
      {/* Reels — injected into the spine's reel bone, drawn under the frame */}
      <Container
        ref={reelContainerRef}
        name="reel_container"
        x={REELSET_X_OFFSET}
        y={REELSET_Y_OFFSET}
        sortableChildren
        eventMode="passive"
      >
        <ReelSet
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dividerDimensions={dividerDimensions}
          reelSpacing={reelSpacing}
        />

        {/* Dims the board while winning symbols are highlighted. Oversized on
            purpose: this container is clipped to the frame window by
            game_view's `reels_window_clip`, so the dim covers the window
            exactly, edge to edge. */}
        <DimReelSet
          ref={dimReelSetRef}
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dividerDimensions={dividerDimensions}
          paddingMultiplier={0.1}
        />
      </Container>

      {/* Overlay — injected into the spine's overlay slot, drawn above the frame */}
      <Container
        ref={overlayContainerRef}
        name="overlay_reel_container"
        x={REELSET_X_OFFSET}
        y={REELSET_Y_OFFSET}
        zIndex={2}
        sortableChildren
        eventMode="passive"
      >
        <OverlayReelSet
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          reelSpacing={reelSpacing}
        />

        {FEATURES_CONFIG.WIN_LINE_DIGIT.ENABLED && (
          <Container name="win_text_overlay" zIndex={6}>
            <WinText
              ref={globalLineWinTextRef}
              fontSizeMultiplier={WIN_TEXT_CONFIG.GLOBAL_WIN_FONT_SIZE}
              hideOnBigWin={true}
            />
            <WinText
              ref={lineWinTextRef}
              fontSizeMultiplier={WIN_TEXT_CONFIG.LINE_WIN_FONT_SIZE}
              hideOnBigWin={true}
            />
          </Container>
        )}

        {FEATURES_CONFIG.WIN_BOX.ENABLED && (
          <WinBoxContainer
            ref={winBoxContainerRef}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            reelSpacing={reelSpacing}
          />
        )}
      </Container>

      {/* Golden Mystery trail layer — the multiplier's bolts to the coins it
          multiplies, and the collector's to the ones it takes.

          ── Why it is HERE, and why zIndex 2 ──

          A trail runs from one cell to another, so it belongs to neither, and it
          cannot be drawn inside either: FXManager emits its particles into the
          layer ITSELF, not into the travelling wrapper, so a layer sorted under
          the board draws the trail beneath the very cells it connects. These run
          along a single reel, so that hid them completely.

          `game_view` has exactly two other children that draw: the scene spine
          at zIndex 1 — which is where the reels and the overlay actually live,
          both reparented into its slots — and the tap surface at 0. So beating
          the spine is beating the whole board, and 2 is the next value up. It is
          also what `overlay_reel_container` declares for "above the frame".

          Being inside `game_view` is what keeps it safe: every dialog and popup
          in this game mounts outside this scene, so nothing here can ever draw
          over one. It also inherits the scene's free-spins alpha, so it cannot
          be left hanging over the bonus room. */}
      <Container
        ref={goldenTrailContainerRef}
        name="golden_trail_container"
        zIndex={2}
        sortableChildren
        eventMode="passive"
      />

      {/* Invisible full-screen tap surface for fullscreen */}
      {FEATURES_CONFIG.BACKGROUND.FULLSCREEN_CLICK_SPRITE && (
        <Sprite
          texture={PIXI.Texture.EMPTY}
          x={0}
          y={0}
          width={dimensions.width}
          height={dimensions.height}
          zIndex={0}
          eventMode={backgroundClickIsActionable ? "static" : "none"}
          cursor={backgroundClickIsActionable ? "pointer" : "default"}
          pointertap={handleBackgroundClick}
        />
      )}
    </Container>
  );
});

GameViewSimple.displayName = "GameViewSimple";

export default GameViewSimple;
